<template>
    <div id="ClaimApprovalInfo">
        <loading :active.sync="loading" :can-cancel="false" :is-full-page="true"
            :height="150" :width="150" :color="'#FFFF00'" :background-color="'#000000'" :opacity="0.5">
        </loading>

        <v-row class="mx-0 mt-0">

            <!-- LEFT: Claim Info Card -->
            <v-col cols="12" md="6">
                <div class="la-card">
                    <div class="la-card-header">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        {{ $t('selfserviceclaimapproval.claimapproval') }}
                    </div>
                    <div class="la-card-body">
                        <div class="la-row">
                            <span class="la-label">{{ $t('columns.staff') }}</span>
                            <span class="la-value">{{ claimApprovalRecord.staffno }} — {{ claimApprovalRecord.staffname }}</span>
                        </div>
                        <div class="la-row">
                            <span class="la-label">{{ $t('generateunpaidleaverecord.leavetype') }}</span>
                            <span class="la-value">{{ claimApprovalRecord.clmtypdesc }}</span>
                        </div>
                        <div class="la-row">
                            <span class="la-label">{{ $t('claimrecordlisting.receiptdate') }}</span>
                            <span class="la-value">{{ claimApprovalRecord.receiptdate }}</span>
                        </div>
                        <div class="la-row">
                            <span class="la-label">{{ $t('claimrecordlisting.receiptno') }}</span>
                            <span class="la-value">{{ claimApprovalRecord.receiptno || '-' }}</span>
                        </div>
                        <div class="la-row">
                            <span class="la-label">{{ $t('claimrecordlisting.chargecode') }}</span>
                            <span class="la-value">{{ claimApprovalRecord.chargecodedesc || '-' }}</span>
                        </div>

                        <!-- Medical category -->
                        <div v-if="claimApprovalRecord.clmtypecatid == 1" class="la-row">
                            <span class="la-label">{{ $t('panelclinic') }}</span>
                            <span class="la-value">
                                <span v-if="claimApprovalRecord.panelclinicdesc">{{ claimApprovalRecord.panelclinicdesc }}</span>
                                <span v-else-if="claimApprovalRecord.clinicname">{{ claimApprovalRecord.clinicname }}</span>
                                <span v-else>-</span>
                            </span>
                        </div>

                        <!-- Mileage category -->
                        <div v-if="claimApprovalRecord.clmtypecatid == 2" class="la-row">
                            <span class="la-label">{{ $t('setupclaimlimit.vehichletype') }}</span>
                            <span class="la-value">{{ claimApprovalRecord.vehtype || '-' }}</span>
                        </div>
                        <div v-if="claimApprovalRecord.clmtypecatid == 2" class="la-row">
                            <span class="la-label">{{ $t('claimrecordlisting.fromlocation') }}</span>
                            <span class="la-value">{{ claimApprovalRecord.fromloc || '-' }}</span>
                        </div>
                        <div v-if="claimApprovalRecord.clmtypecatid == 2" class="la-row">
                            <span class="la-label">{{ $t('claimrecordlisting.tolocation') }}</span>
                            <span class="la-value">{{ claimApprovalRecord.toloc || '-' }}</span>
                        </div>
                        <div v-if="claimApprovalRecord.clmtypecatid == 2" class="la-row">
                            <span class="la-label">{{ $t('claimrecordlisting.distanceinkm') }}</span>
                            <span class="la-value">{{ claimApprovalRecord.distance || '-' }}</span>
                        </div>

                        <div class="la-row">
                            <span class="la-label">{{ $t('columns.remark') }}</span>
                            <span class="la-value">{{ claimApprovalRecord.clmremark || '-' }}</span>
                        </div>
                        <div class="la-row">
                            <span class="la-label">{{ $t('recdisciplinaryrec.supportingdoc') }}</span>
                            <span class="la-value">
                                <span v-if="claimApprovalRecord.filetype" class="la-link" @click="loadAttachment(claimApprovalRecord.filetype, claimApprovalRecord.receiptattach)">
                                    {{ claimApprovalRecord.filetype }} <v-icon small>mdi-eye</v-icon>
                                </span>
                                <span v-else>-</span>
                            </span>
                        </div>
                    </div>
                    <!-- Remark + Action bar -->
                    <div class="la-card-body" style="border-top:1px solid #F1F5F9; padding-top:12px;">
                        <v-text-field v-model="claimApprovalRecord.aprvremark" :label="$t('selfserviceleaveapproval.approrrej')" outlined dense background-color="white"></v-text-field>
                    </div>
                    <div class="la-action-bar">
                        <v-btn class="fbtn fbtn-success" @click="approve(claimApprovalRecord)">
                            <v-icon size="15" class="mr-1">mdi-check</v-icon>{{ $t('taskapprovemobile.approve') }}
                        </v-btn>
                        <v-btn class="fbtn fbtn-danger" @click="reject(claimApprovalRecord)">
                            <v-icon size="15" class="mr-1">mdi-close</v-icon>{{ $t('taskapprovemobile.reject') }}
                        </v-btn>
                        <v-btn class="fbtn fbtn-secondary" @click="finalApprove(claimApprovalRecord)">
                            <v-icon size="15" class="mr-1">mdi-check-all</v-icon>{{ $t('taskapprovemobile.finalapprove') }}
                        </v-btn>
                    </div>
                </div>
            </v-col>

            <!-- RIGHT: Amount Cards + Approval Info -->
            <v-col cols="12" md="6">

                <!-- Approved Amount Card -->
                <div class="la-card mb-3">
                    <div class="la-card-header la-card-header-green">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                        {{ $t('selfserviceclaimapproval.aprvamount') }}
                    </div>
                    <div class="la-card-body">
                        <div class="amt-grid">
                            <span class="amt-label">{{ $t('claimrecordlisting.approvedclaimamountbeforetax') }}</span>
                            <span class="amt-value">{{ formatAmt(claimApprovalRecord.aprvamtb4tax) }}</span>
                            <span class="amt-label">{{ $t('claimrecordlisting.approvedtaxamount') }}</span>
                            <span class="amt-value">{{ formatAmt(claimApprovalRecord.aprvtaxamt) }}</span>
                            <span class="amt-label amt-label-total">{{ $t('claimrecordlisting.approvedtotalamount') }}</span>
                            <div class="amt-value">
                                <v-text-field v-model="claimApprovalRecord.aprvamt" type="number"
                                    :label="$t('columns.totalamount')" outlined dense
                                    @change="inputApproveTotalAmount" background-color="white"
                                    hide-details style="max-width:160px; margin-top:-4px; font-size:1rem; font-weight:700;">
                                </v-text-field>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Receipt Amount Card -->
                <div class="la-card mb-3">
                    <div class="la-card-header la-card-header-blue">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                        {{ $t('selfserviceclaimapproval.recamount') }}
                    </div>
                    <div class="la-card-body">
                        <div class="amt-grid">
                            <span class="amt-label">{{ $t('claimrecordlisting.receiptcurrency') }}</span>
                            <span class="amt-value">{{ claimApprovalRecord.crcydesc }}</span>
                            <span class="amt-label">{{ $t('claimrecordlisting.claimamountbeforetax') }}</span>
                            <span class="amt-value">{{ formatAmt(claimApprovalRecord.receiptclmamtb4tax) }}</span>
                            <span class="amt-label">{{ $t('claimrecordlisting.taxamount') }}</span>
                            <span class="amt-value">{{ formatAmt(claimApprovalRecord.receiptclmtaxamt) }}</span>
                            <span class="amt-label amt-label-total">{{ $t('claimrecordlisting.totalclaimamount') }}</span>
                            <span class="amt-value amt-value-total">{{ formatAmt(claimApprovalRecord.receiptclmtotalamt) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Submission & Approval Info Card -->
                <div class="la-card">
                    <div class="la-card-header la-card-header-green">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                        {{ $t('selfserviceleaveapproval.subapprinfo') }}
                    </div>
                    <div class="la-card-body">
                        <div class="la-row">
                            <span class="la-label">{{ $t('homemobile.submitted') }}</span>
                            <span class="la-value">{{ $t('selfserviceclaimapproval.by') }} {{ claimApprovalRecord.staffno }} {{ claimApprovalRecord.staffname }} {{ $t('homemobile.timerelatedon') }} {{ claimApprovalRecord.entrytime }}</span>
                        </div>
                        <div class="la-label mt-2 mb-1">{{ $t('selfserviceleaveapproval.approval') }}</div>
                        <div v-for="(item, index) in staffApproveList" :key="index" class="la-approval-row">
                            <span class="la-value">{{ $t('selfserviceclaimapproval.by') }} {{ item.staffcode }} {{ item.psnname }}</span>
                            <span class="la-status-badge" :class="item.aprstatus === 'APPROVED' ? 'badge-approved' : 'badge-rejected'">{{ item.aprstatus }}</span>
                            <span class="la-value ml-2">{{ $t('homemobile.timerelatedon') }} {{ item.aprdate }}</span>
                        </div>
                    </div>
                </div>

            </v-col>
        </v-row>

        <!-- Dialogs -->
        <app-alert-event v-bind:subDialog="subDialog" v-bind:subDialogId="subDialogId"
            v-bind:subDialogTitle="subDialogTitle" v-bind:subDialogText="subDialogText"
            v-bind:subDetailTask="subDetailTask" v-on:saveAction="saveAction" v-on:cancelAction="cancelAction" />
        <app-staff-list v-bind:subDialogStaffList="subDialogStaffList" v-bind:subDialogStaffListParam="subDialogStaffListParam"
            v-on:backAction="backAction" v-on:choosenPerson="choosenPerson" />
        <app-alert-error v-bind:errorDialog="errorDialog" v-bind:errorMessage="errorMessage" v-on:OkPressed="dismissHandler" />
    </div>
</template>

<script>

import AlertEvent from '../common/AlertEvent.vue'
import Loading from "vue-loading-overlay";
import StaffListEvent from '../common/StaffList.vue'
import ErrorMessageSelService from '../../sscomponents/common/ErrorMessage.vue'
import moment from "moment";

export default {
    
    components: {
        "app-alert-event": AlertEvent,
        "app-staff-list": StaffListEvent,
        "app-alert-error": ErrorMessageSelService,
        Loading
    },

    props: {
        claimApprovalRecord: Object,
        staffApproveList: Array
    },

    data(){
        return {
            subDialog: false,
            subDialogTitle: '',
            subDialogText: '',
            subDialogId: null,
            subDetailTask: null,
            loading: false,

            subDialogStaffList: false,
            subDialogStaffListParam: null,

            errorDialog: false,
            errorMessage: ''
        }
    },

    methods: {
        formatAmt(value) {
            if (value === null || value === undefined || value === '') return '';
            const num = parseFloat(value);
            return isNaN(num) ? '' : num.toFixed(2);
        },

        // Error Message Popup Dismiss
        dismissHandler(){
            this.errorDialog = false
            this.errorMessage = ''
        },

        finalApprove(param){
            this.subDialogId = 1 //Final Approve ID
            this.subDialog = true
            this.subDialogTitle = this.$t('selfservicemytask.confirmfinapprove')
            this.subDialogText = this.$t('selfservicemytask.approvefintask')
            this.subDetailTask = param
        },

        reject(param){
            if(param.aprvremark) {
                this.subDialogId = 2 //Reject ID
                this.subDialog = true
                this.subDialogTitle = this.$t('selfservicemytask.confirmrej')
                this.subDialogText = this.$t('selfservicemytask.rejecttask')
                this.subDetailTask = param
            } else {
                this.errorDialog = true
                this.errorMessage = this.$t('selfservicemytask.remcolrequired')
                // console.log(this.$t('selfservicemytask.remcolrequired'))
            }
        },

        approve(param){
            this.subDialogId = 3 //Approve ID
            this.subDialog = true
            this.subDialogTitle = this.$t('selfservicemytask.confirmapprove')
            this.subDialogText = this.$t('selfservicemytask.approvetask')
            this.subDetailTask = param
        },

        saveAction($event, param) {
            // Perform Action to final approve
            if($event == 1){
                this.taskFinalApproval(param)
            }

            // Perform Action to reject
            if($event == 2){
                this.taskApproval('REJECT', param)
            }

            // Perform Action to approve
            if($event == 3){
                this.taskCheckApproval(param)
            }

            this.resetSubDialog();
        },

        cancelAction() {
            this.resetSubDialog();
        },

        resetSubDialog(){
            this.subDialogId = null
            this.subDialog = false
            this.subDialogTitle = ''
            this.subDialogText = ''
            this.subDialogTask = null
            this.subDialogStaffList = false
            this.subDialogStaffListParam = null 
        },

        taskFinalApproval(value){

            let data = []
            data.push(value)

            const vm = this;
            vm.loading = true;
            
            this.$store.dispatch('put', {url: "/finalapprovalworkflow/claim", payload:  data})
            .then((res) => {
                const result = res.data;
                if (result.status === 200) {
                    vm.$router.push({name: 'selfservicemytask'});
                } else {
                    console.error(result.error);
                }

                vm.loading = false;

            }).catch((err) => {
                vm.loading = false;
                console.error(err)
            });

        },

        taskApproval(text, value){

            value.apraction = text
            let data = []
            data.push(value)

            const vm = this;
            vm.loading = true;
            
            this.$store.dispatch('put', {url: "/approvalworkflow/claim/" + this.$store.getters.userId + '/' + this.$store.getters.staffId, payload: data})
            .then((res) => {
                const result = res.data;
                if (result.status === 200) {
                    vm.$router.push({name: 'selfservicemytask'});
                } else {
                    console.error(result.error);
                }

                vm.loading = false;

            }).catch((err) => {
                vm.loading = false;
                console.error(err)
            });

        },

        taskCheckApproval(value){

            const vm = this;
            vm.loading = true;

            this.$store.dispatch('get', {url: "/staffreportingposition/" + value.reqstaffid})
            .then((res) => {
                const result = res.data;

                if (value.level == value.lvlno){
                    vm.taskApproval('APPROVE', value)
                }

                if (value.level != value.lvlno){
                    let res = result
                    // if (res.rec.length == 0 || (value.hrlast == 1 && (value.level - value.lvlno > 1)) 
                    // || res.rec[0].staffId != vm.$store.getters.staffId || value[0].exception == 'Y'){
                    //     vm.staffList(value)
                    // } else {
                    //     vm.checkSelfReportingPosition(value)
                    // }

                    if (value.hrlast == 1 && (value.level - value.lvlno == 1)) {
                        vm.taskApproval('APPROVE', value)
                    } else if (res.rec.length == 0 || result.rec[0].staffid != vm.$store.getters.staffId) {
                        vm.staffList(value)
                    } else {
                        vm.checkSelfReportingPosition(value)
                    }
                }

                vm.loading = false;

            }).catch((err) => {
                vm.loading = false;
                console.error(err)
            });

        },

        checkSelfReportingPosition(data) {

            const vm = this;
            vm.loading = true;
            
            this.$store.dispatch('get', {url: "/staffreportingposition/" + this.$store.getters.staffId})
            .then((res) => {
                const result = res.data;

                if(result.rec.length > 0){
                    vm.taskApproval('APPROVE', data)
                } else {
                    vm.staffList(data)
                }

                vm.loading = false;

            }).catch((err) => {
                vm.loading = false;
                console.error(err)
            });

        },

        staffList(param){
            this.subDialogStaffList = true
            this.subDialogStaffListParam = param 
        },

        // Staff List Action
        backAction(){
            this.subDialogStaffList = false
            this.subDialogStaffListParam = null
        },

        // Fucntion receive from choosen staff for another level approval
        choosenPerson(id, data){
            this.subDialogStaffList = false
            data.refstaffid = id
            this.taskWorkflowExceptionApproval(data)
        },

        taskWorkflowExceptionApproval(claimapproval){

            const vm = this;
            vm.loading = true;
            claimapproval.apraction = 'APPROVE'

            this.$store.dispatch('put', {url: "/approvalworkflowexception/claim/" + this.$store.getters.userId + '/' + this.$store.getters.staffId, payload: [claimapproval]})
            .then((res) => {
                const result = res.data;

                if(result.status == 200){
                    vm.$router.push({name: 'selfservicemytask'});
                } else {
                    console.error(result.error);
                }

                vm.subDialogStaffList = false
                vm.subDialogStaffListParam = null
                vm.loading = false;

            }).catch((err) => {
                vm.loading = false;
                console.error(err)
            });

        },

        loadAttachment(filetype, attachment) {

            if(filetype == 'PDF'){
                const blob = this.b64toBlob(attachment, 'application/pdf', 512)
                const url = URL.createObjectURL(blob);
                window.open(url);
            }

            if(filetype == 'Image'){
                var windowHandle = window.open("");
                windowHandle.document.write("<img src='" + attachment + "' />");
            }

        },

        b64toBlob(b64Data, contentType, sliceSize = 512) {
            contentType = contentType || ''
            sliceSize = sliceSize || 512
            b64Data = b64Data.replace(/^[^,]+,/, '')
            b64Data = b64Data.replace(/\s/g, '')

            var byteCharacters = atob(b64Data)
            var byteArrays = []

            for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            var slice = byteCharacters.slice(offset, offset + sliceSize)

            var byteNumbers = new Array(slice.length)
            for (var i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i)
            }

            var byteArray = new Uint8Array(byteNumbers)

            byteArrays.push(byteArray)
            }

            var blob = new Blob(byteArrays, {
            type: contentType
            })
            // return byteCharacters;
            return blob
        },

        inputApproveTotalAmount(){
            if(this.claimApprovalRecord.crcyid == this.claimApprovalRecord.clmtypecrcyid){
                this.loadCheckClaimBalance();
            } else if (this.claimApprovalRecord.crcyid != this.claimApprovalRecord.clmtypecrcyid) {
                this.getExchangeRate();
            }
        },

        loadCheckClaimBalance() {
            let data = {
                receiptdate: moment(this.claimApprovalRecord.receiptdate, 'DD-MM-YYYY').toDate(),
                staffid: this.$store.getters.staffId,
                clmtypeid: this.claimApprovalRecord.clmtypeid,
                userid: this.$store.getters.userId,
            };
            
            const vm = this;
            this.$store.dispatch('post', { url: "/claim/claimbalancenew", payload: data})
                .then((res) => {
                    //req success --> push res into array
                    vm.claimbalancedetail = res.data.rec;

                    vm.claimApprovalRecord.oktoclaim = res.data.rec[0].oktoclaim;
                    vm.claimApprovalRecord.staffid = res.data.rec[0].staffid;
                    vm.claimApprovalRecord.clmtypeid = res.data.rec[0].clmtypeid;

                    // if the receipt currency and claim currency is similar
                    if((parseInt(this.claimApprovalRecord.aprvamt) > this.claimApprovalRecord.oktoclaim) && 
                        (parseInt(this.claimApprovalRecord.aprvamt) > this.claimApprovalRecord.receiptaprvamt) &&
                        (this.claimApprovalRecord.crcyid == this.claimApprovalRecord.clmtypecrcyid)){
                        this.claimApprovalRecord.aprvamt = this.claimApprovalRecord.oktoclaim

                        let convertamount =  parseFloat(this.claimApprovalRecord.aprvamt) *
                                            parseFloat(1);

                        this.claimApprovalRecord.receiptaprvamt = parseFloat(convertamount.toFixed(2));

                        //calculate the converted approve before tax amount if oktoclaim is used as converted approved total amount
                        let newaprvamtb4tax = (parseFloat(this.claimApprovalRecord.aprvamt) / parseFloat(this.claimApprovalRecord.clmtotalamt)) * parseFloat(this.claimApprovalRecord.clmamtb4tax)
                        this.claimApprovalRecord.aprvamtb4tax = parseFloat(newaprvamtb4tax.toFixed(2));
                        this.claimApprovalRecord.receiptaprvamtb4tax = this.claimApprovalRecord.aprvamtb4tax 
                    }
                    // if the receipt currency and claim currency is different
                    else if((parseInt(this.claimApprovalRecord.aprvamt) > this.claimApprovalRecord.oktoclaim) && 
                    (parseInt(this.claimApprovalRecord.aprvamt) > this.claimApprovalRecord.receiptaprvamt) &&
                    (this.claimApprovalRecord.crcyid != this.claimApprovalRecord.clmtypecrcyid)){
                        this.claimApprovalRecord.aprvamt = this.claimApprovalRecord.oktoclaim

                        let convertamount =  parseFloat(this.claimApprovalRecord.aprvamt) /
                                            parseFloat(this.claimApprovalRecord.rate);

                        this.claimApprovalRecord.receiptaprvamt = parseFloat(convertamount.toFixed(2));

                        //calculate the converted approve before tax amount if oktoclaim is used as converted approved total amount

                        let newaprvamtb4tax = (parseFloat(this.claimApprovalRecord.aprvamt) / parseFloat(this.claimApprovalRecord.clmtotalamt)) * parseFloat(this.claimApprovalRecord.clmamtb4tax)
                        this.claimApprovalRecord.aprvamtb4tax = parseFloat(newaprvamtb4tax.toFixed(2));
                        this.claimApprovalRecord.receiptaprvamtb4tax = this.claimApprovalRecord.aprvamtb4tax 
                        
                    //if the claim amount is within the oktoclaim amount
                    } else {
                        if (parseInt(this.claimApprovalRecord.aprvamt) > this.claimApprovalRecord.receiptclmtotalamt) {
                            this.claimApprovalRecord.aprvamt = this.claimApprovalRecord.receiptclmtotalamt
                            this.claimApprovalRecord.receiptaprvamt = this.claimApprovalRecord.receiptclmtotalamt

                            let newaprvamtb4tax = (parseFloat(this.claimApprovalRecord.aprvamt) / parseFloat(this.claimApprovalRecord.clmtotalamt)) * parseFloat(this.claimApprovalRecord.clmamtb4tax)

                            this.claimApprovalRecord.aprvamtb4tax = parseFloat(newaprvamtb4tax.toFixed(2));
                            this.claimApprovalRecord.receiptaprvamtb4tax = this.claimApprovalRecord.aprvamtb4tax 

                        } else {
                            this.claimApprovalRecord.receiptaprvamt = this.claimApprovalRecord.aprvamt
                            
                            let newaprvamtb4tax = (parseFloat(this.claimApprovalRecord.aprvamt) / parseFloat(this.claimApprovalRecord.clmtotalamt)) * parseFloat(this.claimApprovalRecord.clmamtb4tax)

                            this.claimApprovalRecord.aprvamtb4tax = parseFloat(newaprvamtb4tax.toFixed(2));
                            this.claimApprovalRecord.receiptaprvamtb4tax = this.claimApprovalRecord.aprvamtb4tax 
                            
                        }
                    }
            }).catch((err) => {
                //req failure --> emit error
                vm.isErr = true;
                vm.errMsg = err;
                vm.$emit("error", { isErr: vm.isErr, errMsg: vm.errMsg });
            });
        },

        getExchangeRate(){
            const vm = this;

            this.$store.dispatch('get', {
                url: "/exchratecurrency/" + this.claimApprovalRecord.crcyid  + "/" + this.claimApprovalRecord.clmtypecrcyid  
            + "/" + this.claimApprovalRecord.exchrateid + "/" + this.claimApprovalRecord.receiptdate 
            + "/" + this.claimApprovalRecord.receiptdate
            }).then(res => {

                vm.exchangeItems = res.data.rec;

                if (res.data.rec.length > 0 ) {
                        vm.claimApprovalRecord.rate = res.data.rec[0].rate;
                        vm.claimApprovalRecord.sqlAll = res.data.rec[0].sqlAll;
                        vm.claimApprovalRecord.sqlAll2 = res.data.rec[0].sqlAll2;

                if(this.claimApprovalRecord.sqlAll == '1') {

                    this.loadCheckClaimBalance();

                        if(this.claimApprovalRecord.receiptclmamtb4tax != "" ){

                        let convertedrate = this.claimApprovalRecord.rate 

                        let currentAmount =  parseFloat(this.claimApprovalRecord.receiptclmamtb4tax) *
                                            parseFloat(convertedrate);

                        this.claimApprovalRecord.clmamtb4tax = parseFloat(currentAmount.toFixed(2));
                        this.claimApprovalRecord.aprvamtb4tax = parseFloat(currentAmount.toFixed(2));
                        this.claimApprovalRecord.rate = convertedrate;
                        }

                    if(this.claimApprovalRecord.receiptclmtaxamt != "" || this.claimApprovalRecord.receiptclmtaxamt != null){

                        let convertedrate = this.claimApprovalRecord.rate 

                        let currentAmount2 =  parseFloat(this.claimApprovalRecord.receiptclmtaxamt) *
                                            parseFloat(convertedrate);

                        this.claimApprovalRecord.clmtaxamt = parseFloat(currentAmount2.toFixed(2));
                        this.claimApprovalRecord.aprvtaxamt = parseFloat(currentAmount2.toFixed(2));
                        this.claimApprovalRecord.rate = convertedrate;
                        }

                    if(this.claimApprovalRecord.receiptclmtotalamt != ""  ){
                        let convertedrate = this.claimApprovalRecord.rate 

                        let currentAmount3 =  parseFloat(this.claimApprovalRecord.receiptclmtotalamt) *
                                            parseFloat(convertedrate);

                        this.claimApprovalRecord.clmtotalamt = parseFloat(currentAmount3.toFixed(2));
                        this.claimApprovalRecord.aprvamt = parseFloat(currentAmount3.toFixed(2));
                        this.claimApprovalRecord.rate = convertedrate;

                        }
                }

                else if (this.claimApprovalRecord.sqlAll2 == '2') {

                    this.loadCheckClaimBalance();
                
                        if(this.claimApprovalRecord.receiptclmamtb4tax != "" ){

                        let convertedrate = parseFloat(1 / this.claimApprovalRecord.rate);

                        let currentAmount =  parseFloat(this.claimApprovalRecord.receiptclmamtb4tax) *
                                            parseFloat(convertedrate);

                        this.claimApprovalRecord.clmamtb4tax = parseFloat(currentAmount.toFixed(2));
                        this.claimApprovalRecord.aprvamtb4tax = parseFloat(currentAmount.toFixed(2));
                        this.claimApprovalRecord.rate = convertedrate;
                        }

                        if(this.claimApprovalRecord.receiptclmtaxamt != "" ){

                            let convertedrate = parseFloat(1 / this.claimApprovalRecord.rate);

                            let currentAmount2 =  parseFloat(this.claimApprovalRecord.receiptclmtaxamt) *
                                                parseFloat(convertedrate);

                            this.claimApprovalRecord.clmtaxamt = parseFloat(currentAmount2.toFixed(2));
                            this.claimApprovalRecord.aprvtaxamt = parseFloat(currentAmount2.toFixed(2));
                            this.claimApprovalRecord.rate = convertedrate;
                            }

                        if(this.claimApprovalRecord.receiptclmtotalamt != "" ){

                            let convertedrate = parseFloat(1 / this.claimApprovalRecord.rate);

                            let currentAmount3 =  parseFloat(this.claimApprovalRecord.receiptclmtotalamt) *
                                                parseFloat(convertedrate);

                            this.claimApprovalRecord.clmtotalamt = parseFloat(currentAmount3.toFixed(2));
                            this.claimApprovalRecord.aprvamt = parseFloat(currentAmount3.toFixed(2));
                            this.claimApprovalRecord.rate = convertedrate;
                            }
                        }  
                } else {

                        if (res.data.rec.length == 0) {
                        // alert if no exchange items are found
                            this.$dialog.alert("Unable to find exchange rate. Please setup exchange rate at Exchange Rate Set before save record").then((dialog) => {
                            console.log(dialog); 
                        });
                    } 
                }
            })
        }
    },
}

</script>

<style>
.la-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #E8EDF5;
    box-shadow: 0 1px 6px rgba(0,0,0,0.05);
    margin-bottom: 12px;
    overflow: hidden;
}
.la-card-header {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #F1F5F9;
    padding: 10px 16px;
    font-size: 0.82rem;
    font-weight: 600;
    color: #1a3a5c;
    letter-spacing: normal;
    border-bottom: 1px solid #E8EDF5;
}
.la-card-header-green { background: linear-gradient(135deg, #059669, #15803D); color: #fff; }
.la-card-header-blue  { background: linear-gradient(135deg, #4F6BFF, #7C3AFF); color: #fff; }
.la-card-header-green svg, .la-card-header-blue svg { stroke: #fff; }
.la-card-body { padding: 12px 16px; }
.la-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 8px;
    font-size: 0.82rem;
    letter-spacing: normal;
}
.la-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #546e7a;
    min-width: 130px;
    flex-shrink: 0;
    padding-top: 1px;
    letter-spacing: normal;
}
.la-value { font-size: 0.82rem; color: #1a3a5c; letter-spacing: normal; }
.la-amount { font-weight: 600; color: #059669; }
.la-amount-total { font-weight: 700; color: #16A34A; font-size: 0.9rem; }
.la-link { color: #4F6BFF; cursor: pointer; }
.la-link:hover { text-decoration: underline; }
.la-action-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #F8FAFF;
    border-top: 1px solid #E8EDF5;
    padding: 12px 16px;
    border-radius: 0 0 12px 12px;
    flex-wrap: wrap;
}
.fbtn {
    min-width: 100px !important;
    height: 36px !important;
    border-radius: 8px !important;
    font-size: 0.82rem !important;
    font-weight: 600 !important;
    text-transform: none !important;
    letter-spacing: 0.02em !important;
    box-shadow: none !important;
}
.fbtn-success { background: #16A34A !important; color: #fff !important; }
.fbtn-success:hover { background: #15803D !important; }
.fbtn-danger  { background: #E11D48 !important; color: #fff !important; }
.fbtn-danger:hover  { background: #BE123C !important; }
.fbtn-secondary { background: #4F6BFF !important; color: #fff !important; }
.fbtn-secondary:hover { background: #3B55E6 !important; }
.la-approval-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    flex-wrap: wrap;
    font-size: 0.82rem;
}
.la-status-badge {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 700;
    border-radius: 10px;
    padding: 2px 8px;
}
.badge-approved { background: #DCFCE7; color: #059669; }
.badge-rejected { background: #FEE2E2; color: #DC2626; }

/* Amount card grid — labels and values always aligned */
.amt-grid {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 6px 16px;
    align-items: center;
}
.amt-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #546e7a;
    letter-spacing: normal;
    padding: 2px 0;
}
.amt-value {
    font-size: 0.82rem;
    color: #1a3a5c;
    font-weight: 600;
    letter-spacing: normal;
    text-align: right;
    white-space: nowrap;
}
.amt-label-total {
    color: #1a3a5c;
    font-weight: 700;
    border-top: 1px solid #E8EDF5;
    padding-top: 8px;
    margin-top: 2px;
}
.amt-value-total {
    color: #16A34A;
    font-size: 0.95rem;
    font-weight: 700;
    border-top: 1px solid #E8EDF5;
    padding-top: 8px;
    margin-top: 2px;
}
/* Bigger font for editable approved total amount */
.amt-grid .v-text-field input {
    font-size: 1rem !important;
    font-weight: 700 !important;
    color: #16A34A !important;
}
</style>